type HexColor = string;

interface RGBColor {
  red: number;
  green: number;
  blue: number;
}

interface HSLColor {
  hue: number;
  saturation: number;
  lightness: number;
}

function componentToHex(c: number): HexColor {
  const hex = c.toString(16);
  return hex.length == 1
    ? "0" + hex
    : hex;
}

function luminance({ red, green, blue }: RGBColor): number {
  const a = [red, green, blue].map((v) => {
    v /= 255;

    return v <= 0.03928
      ? v / 12.92
      : Math.pow((v + 0.055) / 1.055, 2.4);
  });

  return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
}

export function rgbToHex({ red, green, blue }: RGBColor): HexColor {
  return `#${componentToHex(red)}${componentToHex(green)}${componentToHex(blue)}`;
}

export function hexToRgb(hex: HexColor): RGBColor {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

  return result
    ? {
        red: parseInt(result[1], 16),
        green: parseInt(result[2], 16),
        blue: parseInt(result[3], 16),
      }
    : { red: 0, green: 0, blue: 0 };
}

export function rgbToHsl({ red, green, blue }: RGBColor): HSLColor {
  red /= 255;
  green /= 255;
  blue /= 255;

  const l = Math.max(red, green, blue);
  const s = l - Math.min(red, green, blue);
  const h = s
    ? l === red
      ? (green - blue) / s
      : l === green
        ? 2 + (blue - red) / s
        : 4 + (red - green) / s
    : 0;

  return {
    hue: 60 * h < 0 ? (60 * h + 360) : (60 * h),
    saturation: 100 * (s ? l <= 0.5 ? s / (2 * l - s) : s / (2 - (2 * l - s)) : 0),
    lightness: (100 * (2 * l - s)) / 2,
  };
}

export function hslToRgb({ hue, saturation, lightness }: HSLColor): RGBColor {
  saturation /= 100;
  lightness /= 100;

  const a = saturation * Math.min(lightness, 1 - lightness);
  const k = (n: number) => (n + hue / 30) % 12;
  const f = (n: number) => lightness - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));

  return {
    red: Math.floor(255 * f(0)),
    green: Math.floor(255 * f(8)),
    blue: Math.floor(255 * f(4)),
  };
}

export function getTextColor(hexColor: string, darkColor = "#000000", lightColor = "#ffffff") {
  const { red, green, blue } = hexToRgb(hexColor);

  return (red * 0.299 + green * 0.587 + blue * 0.114) > 115
    ? darkColor
    : lightColor;
}

export function getContrast(rgb1: RGBColor, rgb2: RGBColor): number {
  const lum1 = luminance(rgb1);
  const lum2 = luminance(rgb2);
  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);

  return (brightest + 0.05) / (darkest + 0.05);
}

export function makeReadableColor(baseColor: HexColor, backgroundColor: HexColor, thresholdContrast: number, luminanceCoarseness: number): HexColor {
  const backgroundRgb = hexToRgb(backgroundColor);

  const isBackgroundDark = getTextColor(backgroundColor) === "#ffffff";
  const lightnessStep = isBackgroundDark ? luminanceCoarseness : (-1 * luminanceCoarseness);

  let testedTextHsl = rgbToHsl(hexToRgb(baseColor));

  while (isBackgroundDark ? (testedTextHsl.lightness + lightnessStep) < 100 : (testedTextHsl.lightness + lightnessStep) > 0) {
    if (getContrast(hslToRgb(testedTextHsl), backgroundRgb) > thresholdContrast)
      break;

    const lightnessDistance = Math.sign(lightnessStep) > 0
      ? (100 - testedTextHsl.lightness) / 100
      : (testedTextHsl.lightness / 100);
    const lightnessDeltaFactor = lightnessDistance / lightnessStep;
    const saturationDistance = 100 - testedTextHsl.saturation;
    const saturationIncrease = saturationDistance * Math.abs(lightnessDeltaFactor);

    testedTextHsl = { ...testedTextHsl, lightness: testedTextHsl.lightness + lightnessStep, saturation: Math.min(100, testedTextHsl.saturation + saturationIncrease) };
  }

  return rgbToHex(hslToRgb(testedTextHsl));
}

export function generateColorGradient(colors: HexColor[], steps: number): HexColor[] {
  if (colors.length < 2)
    throw new Error("At least two colors are required to generate a gradient.");

  if (steps < colors.length)
    throw new Error("The number of steps must be greater than or equal to the number of colors.");

  const rgbColors = colors.map(hexToRgb);

  const results: RGBColor[] = [];

  for (let i = 0; i < steps; i++) {
    const progress = i / (steps - 1);
    const colorProgress = progress * (colors.length - 1);
    const mixRatio = colorProgress - Math.floor(colorProgress);
    const startColorIdx = Math.floor(colorProgress);
    const startColor = rgbColors[startColorIdx];
    const endColor = rgbColors[startColorIdx + 1];

    if (!endColor) {
      results.push(startColor);
      continue;
    }

    results.push({
      red: Math.round(startColor.red * (1 - mixRatio) + endColor.red * mixRatio),
      green: Math.round(startColor.green * (1 - mixRatio) + endColor.green * mixRatio),
      blue: Math.round(startColor.blue * (1 - mixRatio) + endColor.blue * mixRatio),
    });
  }

  return results.map(rgbToHex);
}
